import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import LocalShipping from "@mui/icons-material/LocalShipping";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { defaultErrorToast } from "../../../utils/floorUtils";
import {
    buildInterCompanyTransferPayload,
    buildItemOptions,
    buildWarehouseOptions,
    createTransferRow,
    summarizeTransfer,
    validateInterCompanyTransfer,
} from "./interCompanyTransferUtils";

const EMPTY_ITEM_SEARCH = { rowId: "", value: "" };
const ITEM_SEARCH_DEBOUNCE_MS = 300;

const createInitialForm = (currentCompany, companies) => {
    const sourceCompany =
        currentCompany?.name || companies.find((entry) => entry?.name)?.name || "";
    const targetCompany =
        companies.find(
            (entry) => entry?.name && entry.name !== sourceCompany
        )?.name || "";

    return {
        source_company: sourceCompany,
        target_company: targetCompany,
        source_warehouse: "",
        target_warehouse: "",
        posting_date: format(new Date(), "yyyy-MM-dd"),
        posting_time: format(new Date(), "HH:mm:ss"),
        remarks: "",
        items: [],
    };
};

const getValidationMessage = (t, error) => {
    if (Array.isArray(error)) {
        return t(`stockTransfer.validation.${error[0]}`, error[1]);
    }
    return t(`stockTransfer.validation.${error}`);
};

const SectionHeading = ({ number, title, description, action }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
        }}
    >
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: "rgba(225, 73, 84, 0.12)",
                color: "#e14954",
                fontWeight: 800,
            }}
        >
            {number}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
        {action}
    </Box>
);

export default function InterCompanyStockTransferDialog({
    open,
    onClose,
    companies = [],
    currentCompany,
    onCompleted,
}) {
    const { t } = useTranslation();
    const companySignature = companies
        .map((entry) => entry?.name || "")
        .join("|");
    const [form, setForm] = useState(() =>
        createInitialForm(currentCompany, companies)
    );
    const [result, setResult] = useState(null);
    const [itemSearch, setItemSearch] = useState(EMPTY_ITEM_SEARCH);
    const [debouncedItemSearch, setDebouncedItemSearch] = useState("");
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(createInitialForm(currentCompany, companies));
        setResult(null);
        setItemSearch(EMPTY_ITEM_SEARCH);
        setDebouncedItemSearch("");
        setConfirmationOpen(false);
        // companySignature intentionally resets the route when access changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentCompany?.name, companySignature]);

    useEffect(() => {
        const timeout = setTimeout(
            () => setDebouncedItemSearch(itemSearch.value.trim()),
            ITEM_SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(timeout);
    }, [itemSearch.value]);

    const setupQuery = useQuery(
        [
            "inter-company-transfer-setup",
            form.source_company,
            form.target_company,
            form.source_warehouse,
        ],
        async () => {
            const response = await axios.post(
                "method/custom_api.inter_company_stock_transfer.get_inter_company_transfer_setup",
                {
                    source_company: form.source_company,
                    target_company: form.target_company,
                    source_warehouse: form.source_warehouse || undefined,
                }
            );
            return response?.data?.message || {};
        },
        {
            enabled: open && Boolean(form.source_company),
            refetchOnWindowFocus: false,
        }
    );

    const itemSearchQuery = useQuery(
        [
            "inter-company-transfer-item-search",
            form.source_company,
            form.source_warehouse,
            debouncedItemSearch,
        ],
        async () => {
            const response = await axios.post(
                "method/custom_api.inter_company_stock_transfer.search_inter_company_transfer_items",
                {
                    source_company: form.source_company,
                    source_warehouse: form.source_warehouse,
                    item_search: debouncedItemSearch,
                }
            );
            return response?.data?.message || {};
        },
        {
            enabled:
                open &&
                Boolean(
                    form.source_company &&
                        form.source_warehouse &&
                        debouncedItemSearch
                ),
            refetchOnWindowFocus: false,
        }
    );

    const setup = useMemo(() => setupQuery.data || {}, [setupQuery.data]);
    const companyOptions = useMemo(
        () =>
            companies
                .filter((entry) => entry?.name)
                .map((entry) => ({
                    value: entry.name,
                    label: entry.company_name || entry.name,
                })),
        [companies]
    );
    const sourceWarehouseOptions = useMemo(
        () => buildWarehouseOptions(setup.source_warehouses),
        [setup.source_warehouses]
    );
    const targetWarehouseOptions = useMemo(
        () => buildWarehouseOptions(setup.target_warehouses),
        [setup.target_warehouses]
    );
    const selectedItemCodes = useMemo(
        () => new Set(form.items.map((row) => row.item_code).filter(Boolean)),
        [form.items]
    );
    const availableItems = useMemo(
        () =>
            debouncedItemSearch
                ? itemSearchQuery.data?.items || []
                : setup.items || [],
        [debouncedItemSearch, itemSearchQuery.data, setup.items]
    );
    const itemOptions = useMemo(
        () => buildItemOptions(availableItems, form.items),
        [availableItems, form.items]
    );
    const summary = useMemo(() => summarizeTransfer(form.items), [form.items]);
    const validationErrors = useMemo(
        () => validateInterCompanyTransfer(form, setup),
        [form, setup]
    );

    const transferMutation = useMutation(
        async (payload) => {
            const response = await axios.post(
                "method/custom_api.inter_company_stock_transfer.create_inter_company_stock_transfer",
                { payload }
            );
            return response?.data?.message;
        },
        {
            onSuccess: (data) => {
                setResult(data);
                setForm((current) => ({ ...current, items: [] }));
                setConfirmationOpen(false);
                enqueueSnackbar(t("stockTransfer.completedSuccessfully"), {
                    variant: "success",
                });
                setupQuery.refetch();
                onCompleted?.(data);
            },
            onError: (error) => {
                setConfirmationOpen(false);
                defaultErrorToast(error);
            },
        }
    );

    const changeRouteField = (fieldname, value) => {
        setResult(null);
        if (fieldname === "source_company" || fieldname === "source_warehouse") {
            setItemSearch(EMPTY_ITEM_SEARCH);
            setDebouncedItemSearch("");
        }
        setForm((current) => {
            if (fieldname === "source_company") {
                return {
                    ...current,
                    source_company: value,
                    target_company:
                        current.target_company === value
                            ? ""
                            : current.target_company,
                    source_warehouse: "",
                    target_warehouse: "",
                    items: [],
                };
            }
            if (fieldname === "target_company") {
                return {
                    ...current,
                    target_company: value,
                    target_warehouse: "",
                };
            }
            if (fieldname === "source_warehouse") {
                return {
                    ...current,
                    source_warehouse: value,
                    items: [],
                };
            }
            return { ...current, [fieldname]: value };
        });
    };

    const updateRow = (id, patch) => {
        setResult(null);
        setForm((current) => ({
            ...current,
            items: current.items.map((row) =>
                row.id === id ? { ...row, ...patch } : row
            ),
        }));
    };

    const requestConfirmation = () => {
        if (validationErrors.length) {
            enqueueSnackbar(
                getValidationMessage(t, validationErrors[0]),
                { variant: "error" }
            );
            return;
        }
        setConfirmationOpen(true);
    };

    const submitTransfer = () => {
        transferMutation.mutate(
            buildInterCompanyTransferPayload(form, uuidv4())
        );
    };

    const removeRow = (id) => {
        setForm((current) => ({
            ...current,
            items: current.items.filter((row) => row.id !== id),
        }));
    };

    const isBusy = transferMutation.isLoading;

    return (
        <>
            <Dialog
                open={open}
                onClose={isBusy ? undefined : onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{ sx: { minHeight: "82vh" } }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 800,
                    }}
                >
                    <LocalShipping color="primary" />
                    {t("stockTransfer.title")}
                    <IconButton
                        aria-label={t("common.close")}
                        onClick={onClose}
                        disabled={isBusy}
                        sx={{ ml: "auto" }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: "#f5f6fa" }}>
                    {result ? (
                        <Alert
                            severity="success"
                            sx={{ mb: 2 }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => setResult(null)}
                                >
                                    {t("stockTransfer.createAnother")}
                                </Button>
                            }
                        >
                            <Typography sx={{ fontWeight: 800 }}>
                                {t("stockTransfer.completed")}
                            </Typography>
                            <Box>
                                {result.source_warehouse} → {result.target_warehouse}
                            </Box>
                            <Box sx={{ mt: 0.5 }}>
                                {t("stockTransfer.deliveryNote")}: {result.delivery_note}
                                {" · "}
                                {t("stockTransfer.purchaseReceipt")}: {result.purchase_receipt}
                            </Box>
                        </Alert>
                    ) : null}

                    <Paper sx={{ p: 2.5, mb: 2 }}>
                        <SectionHeading
                            number="1"
                            title={t("stockTransfer.chooseRoute")}
                            description={t("stockTransfer.chooseRouteDescription")}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t("stockTransfer.fromCompany")}
                                    value={form.source_company}
                                    disabled={isBusy}
                                    onChange={(event) =>
                                        changeRouteField(
                                            "source_company",
                                            event.target.value
                                        )
                                    }
                                >
                                    {companyOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t("stockTransfer.sourceWarehouse")}
                                    value={form.source_warehouse}
                                    disabled={
                                        isBusy ||
                                        setupQuery.isFetching ||
                                        !form.source_company
                                    }
                                    onChange={(event) =>
                                        changeRouteField(
                                            "source_warehouse",
                                            event.target.value
                                        )
                                    }
                                >
                                    {sourceWarehouseOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t("stockTransfer.toCompany")}
                                    value={form.target_company}
                                    disabled={isBusy}
                                    onChange={(event) =>
                                        changeRouteField(
                                            "target_company",
                                            event.target.value
                                        )
                                    }
                                >
                                    {companyOptions
                                        .filter(
                                            (option) =>
                                                option.value !== form.source_company
                                        )
                                        .map((option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t("stockTransfer.destinationWarehouse")}
                                    value={form.target_warehouse}
                                    disabled={
                                        isBusy ||
                                        setupQuery.isFetching ||
                                        !form.target_company
                                    }
                                    onChange={(event) =>
                                        changeRouteField(
                                            "target_warehouse",
                                            event.target.value
                                        )
                                    }
                                >
                                    {targetWarehouseOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        {setup.currency_compatible === false ? (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {t("stockTransfer.currencyMismatch", {
                                    source: setup.source_currency,
                                    target: setup.target_currency,
                                })}
                            </Alert>
                        ) : null}
                        {setupQuery.isError ? (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {t("stockTransfer.setupFailed")}
                            </Alert>
                        ) : null}
                    </Paper>

                    <Paper sx={{ p: 2.5, mb: 2 }}>
                        <SectionHeading
                            number="2"
                            title={t("stockTransfer.addStock")}
                            description={t("stockTransfer.addStockDescription")}
                            action={
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    disabled={
                                        !form.source_warehouse ||
                                        isBusy ||
                                        setupQuery.isFetching ||
                                        Boolean(result)
                                    }
                                    onClick={() =>
                                        setForm((current) => ({
                                            ...current,
                                            items: [
                                                ...current.items,
                                                createTransferRow(),
                                            ],
                                        }))
                                    }
                                >
                                    {t("stockTransfer.addItem")}
                                </Button>
                            }
                        />
                        {itemSearchQuery.isError ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {t("stockTransfer.itemSearchFailed")}
                            </Alert>
                        ) : null}
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("stockTransfer.item")}</TableCell>
                                        <TableCell>
                                            {t("stockTransfer.available")}
                                        </TableCell>
                                        <TableCell>
                                            {t("stockTransfer.quantity")}
                                        </TableCell>
                                        <TableCell>
                                            {t("stockTransfer.batchSerial")}
                                        </TableCell>
                                        <TableCell width={52} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {form.items.length ? (
                                        form.items.map((row) => {
                                            const options = itemOptions.filter(
                                                (option) =>
                                                    option.value === row.item_code ||
                                                    !selectedItemCodes.has(
                                                        option.value
                                                    )
                                            );
                                            const selectedOption =
                                                itemOptions.find(
                                                    (option) =>
                                                        option.value === row.item_code
                                                ) || null;
                                            const isSearching =
                                                itemSearch.rowId === row.id &&
                                                Boolean(itemSearch.value.trim()) &&
                                                (itemSearch.value.trim() !==
                                                    debouncedItemSearch ||
                                                    itemSearchQuery.isFetching);

                                            return (
                                                <TableRow key={row.id}>
                                                    <TableCell sx={{ minWidth: 340 }}>
                                                        <Autocomplete
                                                            options={options}
                                                            value={selectedOption}
                                                            loading={isSearching}
                                                            disabled={isBusy}
                                                            filterOptions={(values) => values}
                                                            isOptionEqualToValue={(
                                                                option,
                                                                selected
                                                            ) =>
                                                                option.value ===
                                                                selected.value
                                                            }
                                                            getOptionLabel={(option) =>
                                                                option?.label || ""
                                                            }
                                                            onInputChange={(
                                                                event,
                                                                value,
                                                                reason
                                                            ) => {
                                                                if (reason === "input") {
                                                                    setItemSearch({
                                                                        rowId: row.id,
                                                                        value,
                                                                    });
                                                                }
                                                            }}
                                                            onClose={() => {
                                                                setItemSearch((current) =>
                                                                    current.rowId === row.id
                                                                        ? EMPTY_ITEM_SEARCH
                                                                        : current
                                                                );
                                                            }}
                                                            onChange={(event, option) => {
                                                                setItemSearch(
                                                                    EMPTY_ITEM_SEARCH
                                                                );
                                                                updateRow(
                                                                    row.id,
                                                                    option
                                                                        ? {
                                                                              item_code:
                                                                                  option.item_code,
                                                                              item_name:
                                                                                  option.item_name,
                                                                              stock_uom:
                                                                                  option.stock_uom,
                                                                              actual_qty:
                                                                                  option.actual_qty,
                                                                              valuation_rate:
                                                                                  option.valuation_rate,
                                                                              has_serial_no:
                                                                                  option.has_serial_no,
                                                                              has_batch_no:
                                                                                  option.has_batch_no,
                                                                              variant_of:
                                                                                  option.variant_of,
                                                                              qty: "",
                                                                              serial_no: "",
                                                                              batch_no: "",
                                                                          }
                                                                        : createTransferRow({
                                                                              id: row.id,
                                                                          })
                                                                );
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    label={t(
                                                                        "stockTransfer.selectItem"
                                                                    )}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        endAdornment: (
                                                                            <>
                                                                                {isSearching ? (
                                                                                    <CircularProgress
                                                                                        size={18}
                                                                                    />
                                                                                ) : null}
                                                                                {
                                                                                    params
                                                                                        .InputProps
                                                                                        .endAdornment
                                                                                }
                                                                            </>
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        {row.item_name ? (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {row.item_name}
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                        <strong>
                                                            {Number(
                                                                row.actual_qty || 0
                                                            ).toLocaleString()}
                                                        </strong>{" "}
                                                        {row.stock_uom}
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 140 }}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={row.qty}
                                                            disabled={
                                                                isBusy || !row.item_code
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                                step:
                                                                    Number(
                                                                        row.has_serial_no
                                                                    ) === 1
                                                                        ? 1
                                                                        : "any",
                                                            }}
                                                            onChange={(event) =>
                                                                updateRow(row.id, {
                                                                    qty: event.target.value,
                                                                })
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 240 }}>
                                                        {Number(row.has_batch_no) === 1 ? (
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={row.batch_no}
                                                                disabled={isBusy}
                                                                label={t(
                                                                    "stockTransfer.batch"
                                                                )}
                                                                sx={{ mb: 1 }}
                                                                onChange={(event) =>
                                                                    updateRow(row.id, {
                                                                        batch_no:
                                                                            event.target.value,
                                                                    })
                                                                }
                                                            />
                                                        ) : null}
                                                        {Number(row.has_serial_no) === 1 ? (
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                minRows={2}
                                                                size="small"
                                                                value={row.serial_no}
                                                                disabled={isBusy}
                                                                label={t(
                                                                    "stockTransfer.serialNumbers"
                                                                )}
                                                                onChange={(event) =>
                                                                    updateRow(row.id, {
                                                                        serial_no:
                                                                            event.target.value,
                                                                    })
                                                                }
                                                            />
                                                        ) : null}
                                                        {!Number(row.has_batch_no) &&
                                                        !Number(row.has_serial_no)
                                                            ? "—"
                                                            : null}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            color="error"
                                                            aria-label={t(
                                                                "stockTransfer.removeItem"
                                                            )}
                                                            disabled={isBusy}
                                                            onClick={() =>
                                                                removeRow(row.id)
                                                            }
                                                        >
                                                            <DeleteOutline />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                align="center"
                                                sx={{ py: 5, color: "text.secondary" }}
                                            >
                                                {form.source_warehouse
                                                    ? t("stockTransfer.addItemsPrompt")
                                                    : t(
                                                          "stockTransfer.selectWarehousePrompt"
                                                      )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={{ p: 2.5 }}>
                        <SectionHeading
                            number="3"
                            title={t("stockTransfer.reviewTransfer")}
                            description={t(
                                "stockTransfer.reviewTransferDescription"
                            )}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label={t("stockTransfer.postingDate")}
                                    value={form.posting_date}
                                    disabled={isBusy}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(event) =>
                                        setForm((current) => ({
                                            ...current,
                                            posting_date: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    type="time"
                                    label={t("stockTransfer.postingTime")}
                                    value={form.posting_time}
                                    disabled={isBusy}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ step: 1 }}
                                    onChange={(event) =>
                                        setForm((current) => ({
                                            ...current,
                                            posting_time: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t("stockTransfer.remarks")}
                                    value={form.remarks}
                                    disabled={isBusy}
                                    onChange={(event) =>
                                        setForm((current) => ({
                                            ...current,
                                            remarks: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "#f5f6fa",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                flexWrap: "wrap",
                            }}
                        >
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t("stockTransfer.items")}
                                </Typography>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {form.items.length}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t("stockTransfer.totalQuantity")}
                                </Typography>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {summary.totalQty.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {t("stockTransfer.sourceValue")}
                                </Typography>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {summary.totalValue.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}{" "}
                                    {setup.source_currency || ""}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={
                                    isBusy ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <LocalShipping />
                                    )
                                }
                                disabled={
                                    validationErrors.length > 0 ||
                                    isBusy ||
                                    setupQuery.isFetching ||
                                    Boolean(result)
                                }
                                onClick={requestConfirmation}
                                sx={{ ml: "auto" }}
                            >
                                {t("stockTransfer.transferStock")}
                            </Button>
                        </Box>
                        {validationErrors.length > 0 && form.items.length ? (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                {getValidationMessage(t, validationErrors[0])}
                            </Typography>
                        ) : null}
                    </Paper>
                </DialogContent>
            </Dialog>

            <Dialog
                open={confirmationOpen}
                onClose={isBusy ? undefined : () => setConfirmationOpen(false)}
            >
                <DialogTitle>{t("stockTransfer.confirmTitle")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("stockTransfer.confirmMessage")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmationOpen(false)}
                        disabled={isBusy}
                    >
                        {t("stockTransfer.review")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitTransfer}
                        disabled={isBusy}
                    >
                        {isBusy ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            t("stockTransfer.transferStock")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
